<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import { PokerChipIcon } from '@uiKit/icons'

const emit = defineEmits<{
  (e: 'view-all'): void
}>()

type TxStatus = 'Completed' | 'Processing' | 'Pending'

interface TokenTransaction {
  id: string
  propertyName: string
  date: string
  amount: number
  status: TxStatus
}

const STATUS_CONFIG: Record<
  TxStatus,
  { bg: string; colorFrom: string; colorTo: string; amountColor: string }
> = {
  Completed: { bg: '#f5fee7', colorFrom: '#88d66c', colorTo: '#447d0e', amountColor: '#58a40c' },
  Processing: { bg: '#eff8ff', colorFrom: '#90caff', colorTo: '#1570ef', amountColor: '#181d27' },
  Pending: { bg: '#fefce8', colorFrom: '#fde68a', colorTo: '#cb8e03', amountColor: '#181d27' },
}

const transactions: TokenTransaction[] = [
  {
    id: '1',
    propertyName: 'Downtown Dubai Tower',
    date: 'Received · 20 Jan 2026',
    amount: 25,
    status: 'Completed',
  },
  {
    id: '2',
    propertyName: 'Palm Jumeirah Villa',
    date: 'Received · 10 Dec 2025',
    amount: 160,
    status: 'Processing',
  },
  {
    id: '3',
    propertyName: 'Business Bay Condo',
    date: 'Received · 05 Nov 2025',
    amount: 100,
    status: 'Pending',
  },
  {
    id: '4',
    propertyName: 'Downtown Dubai Tower',
    date: 'Received · 20 Jan 2026',
    amount: 45,
    status: 'Completed',
  },
  {
    id: '5',
    propertyName: 'Palm Jumeirah Villa',
    date: 'Received · 10 Dec 2025',
    amount: 140,
    status: 'Processing',
  },
]
</script>

<template>
  <Box direction="col" gap="6" class="trt">
    <Box align="center" justify="between">
      <Text type="page-title" class="trt__title">Recent Token Transactions</Text>
      <Button label="View All" variant="link" size="small" @click="emit('view-all')" />
    </Box>

    <Box direction="col" gap="4" class="trt__list-card">
      <template v-for="(tx, i) in transactions" :key="tx.id">
        <Box align="center" justify="between" class="trt__row">
          <Box align="center" gap="3" grow class="trt__left">
            <Box
              flex
              align="center"
              justify="center"
              class="trt__icon"
              :style="{ background: STATUS_CONFIG[tx.status].bg }"
            >
              <PokerChipIcon
                class="trt__chip"
                :color-from="STATUS_CONFIG[tx.status].colorFrom"
                :color-to="STATUS_CONFIG[tx.status].colorTo"
              />
            </Box>
            <Box direction="col" gap="1" class="trt__info">
              <Text type="body" weight="medium" truncate class="trt__property">
                {{ tx.propertyName }}
              </Text>
              <Text type="caption" weight="medium" color="muted">{{ tx.date }}</Text>
            </Box>
          </Box>
          <Box direction="col" align="end" gap="1" class="trt__right">
            <Text
              type="body"
              weight="semibold"
              align="right"
              class="trt__amount"
              :style="{ color: STATUS_CONFIG[tx.status].amountColor }"
            >
              +{{ tx.amount }} ARVA
            </Text>
            <Text type="caption" weight="medium" color="muted" align="right">{{ tx.status }}</Text>
          </Box>
        </Box>
        <Box v-if="i < transactions.length - 1" class="trt__divider" />
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.trt {
  flex: 1;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.trt__list-card {
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 18px 1.25rem;
}

.trt__row {
  padding: 4px 0;
}

.trt__left {
  min-width: 0;
}

.trt__icon {
  width: 46px;
  height: 46px;
  border-radius: var(--p-border-radius-md);
  flex-shrink: 0;
}

.trt__chip {
  width: 28px;
  height: 28px;
}

.trt__info {
  min-width: 0;
}

.trt__right {
  flex-shrink: 0;
}

.trt__divider {
  height: 1px;
  background: var(--p-content-border-color);
}

@media (max-width: 992px) {
  .trt {
    padding: 1rem;
  }

  .trt__list-card {
    padding: 14px 1rem;
    gap: 14px;
  }

  .trt__title {
    font-size: 15px !important;
  }

  .trt__icon {
    width: 40px;
    height: 40px;
  }

  .trt__chip {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 480px) {
  .trt {
    padding: 14px 0.75rem;
  }

  .trt__list-card {
    padding: 0.75rem 14px;
    gap: 0.75rem;
  }

  .trt__row {
    align-items: flex-start;
  }

  .trt__right {
    padding-top: 2px;
  }

  .trt__property {
    white-space: normal;
  }
}
</style>
